import React from 'react';
import { Grid, Typography } from '@mui/material';
import { Form } from 'formik';
import * as Yup from 'yup';
import { isSquaredDisplay } from 'components/content/models/ContentModule';
import { MuiForm } from 'lib/Form';
import { TextFormField } from 'lib/form/fields/TextFormField';
import { ImageFormField } from 'lib/form/fields/ImageFormField';
import { SwitchFormField } from 'lib/form/fields/SwitchFormField';
import { MuiFormLabel } from 'lib/form/MuiFormLabel';
import { getImageAspect, getImagePickerDescriptionByDisplay, MarketingMessageProps } from './base';
import { Row } from 'ui/Flex';
import { LoadingButton } from 'ui/buttons/LoadingButton';

interface IContent {
    imageUrl: string;
    pageTitle: string;
    title: string;
    subtitle: string;
    body: string;
    buttonTitle: string;
    buttonUrl: string;
}

interface INativeItemProperties {
    imageUrl: string;
    title?: string;
    subtitle?: string;
    subtitleTwo?: string;
    invertTextColour?: boolean;
    content: IContent;
}

function getFormFromContent(content: IContent) {
    if (!content) {
        return {};
    }
    return Object.entries(content).reduce((acc, [field, item]) => {
        acc[`content.${field}`] = item;
        return acc;
    }, {} as any);
}

export const NativeMessageProperties: React.FC<MarketingMessageProps<INativeItemProperties>> = props => {
    const { display, properties = {} as INativeItemProperties, onSubmit, name, contextForm } = props;
    const hideMarketingMessageOverlay = isSquaredDisplay(display);

    const initialValues = React.useMemo(
        () => ({
            name: name || '',
            imageUrl: properties.imageUrl || '',
            title: properties.title || '',
            subtitle: properties.subtitle || '',
            subtitleTwo: properties.subtitleTwo || '',
            invertTextColour: properties.invertTextColour || false,
            showTextBackgroundBar: (properties as any).showTextBackgroundBar || false,
            ...getFormFromContent(properties.content)
        }),
        [name, properties]
    );

    const validationSchema = React.useMemo(() => {
        const schema: any = {
            imageUrl: Yup.string().required('This field is required.').url('Invalid URL.'),
            name: Yup.string().required('This field is required.'),
            'content.pageTitle': Yup.string().required('This field is required.'),
            'content.title': Yup.string().required('This field is required.'),
            'content.body': Yup.string().required('This field is required.')
        };
        return Yup.object().shape(schema);
    }, []);

    const handleSubmit = React.useCallback(
        (data: any) => {
            const formattedData = Object.entries(data).reduce(
                (acc: any, [field, item]) => {
                    if (!!item || typeof item === 'boolean') {
                        if (field.startsWith('content.')) {
                            acc.content[field.substring(8, field.length)] = item;
                        } else {
                            acc[field] = item;
                        }
                    }
                    return acc;
                },
                { content: {} }
            );
            onSubmit(formattedData);
        },
        [onSubmit]
    );

    return (
        <MuiForm onSubmit={handleSubmit} initialValues={initialValues} validationSchema={validationSchema}>
            {({ submitForm, isSubmitting, isValid }) => (
                <Form>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <ImageFormField
                                name="imageUrl"
                                label={<MuiFormLabel required>Image</MuiFormLabel>}
                                aspect={getImageAspect(display)}
                                accept={['image/jpeg', 'image/png', 'image/gif']}
                                description={getImagePickerDescriptionByDisplay(display)}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextFormField
                                name="name"
                                label={<MuiFormLabel required>Reporting Name</MuiFormLabel>}
                                description="This code is used to track the number of marketing module item taps on anchor screen"
                            />
                        </Grid>
                        {!hideMarketingMessageOverlay && (
                            <>
                                <Grid item xs={12}>
                                    <Grid container direction="column">
                                        <Typography color="primary">Marketing message overlay</Typography>
                                    </Grid>
                                </Grid>
                                <Grid item xs={12}>
                                    <TextFormField name="title" label="Title" />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextFormField name="subtitle" label="Subtitle" />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextFormField name="subtitleTwo" label="Subtitle two" />
                                </Grid>
                                <Grid item xs={12}>
                                    <SwitchFormField name="invertTextColour" label="Invert text colour" />
                                </Grid>
                                <Grid item xs={12}>
                                    <SwitchFormField
                                        name="showTextBackgroundBar"
                                        label="Show text background bar"
                                    />
                                </Grid>
                            </>
                        )}
                        <Grid item xs={12}>
                            <Grid container direction="column">
                                <Typography color="primary">Content</Typography>
                                <Typography variant="caption">
                                    Configure what you want your users to see when they tap on your marketing
                                    item
                                </Typography>
                            </Grid>
                        </Grid>
                        <Grid item xs={12}>
                            <ImageFormField
                                name="content.imageUrl"
                                label="Content image"
                                aspect={getImageAspect()}
                                accept={['image/jpeg', 'image/png', 'image/gif']}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextFormField
                                name="content.pageTitle"
                                label={<MuiFormLabel required>Content page title</MuiFormLabel>}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextFormField
                                name="content.title"
                                label={<MuiFormLabel required>Content title</MuiFormLabel>}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextFormField name="content.subtitle" label="Content subtitle" />
                        </Grid>
                        <Grid item xs={12}>
                            <TextFormField
                                name="content.body"
                                label={<MuiFormLabel required>Content body</MuiFormLabel>}
                                multiline
                                rows={4}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextFormField name="content.buttonTitle" label="Button title" />
                        </Grid>
                        <Grid item xs={12}>
                            <TextFormField name="content.buttonUrl" label="Button url" />
                        </Grid>
                        {contextForm()}
                        <Grid item xs={12}>
                            <Row gutter align="flex-end" valign="center">
                                <LoadingButton
                                    color="primary"
                                    variant="contained"
                                    disabled={isSubmitting || !isValid}
                                    loading={isSubmitting}
                                    onClick={submitForm}
                                >
                                    Submit
                                </LoadingButton>
                            </Row>
                        </Grid>
                    </Grid>
                </Form>
            )}
        </MuiForm>
    );
};
